import React, { ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Box, Grid, Typography } from '@mui/material';
import { DateTime } from 'luxon';

export interface PromotionStatsFCProps {
  strings: {
    orders: string;
    customers: string;
    sales: string;
    update: string;
    noOrders: string;
    newCustomers: string;
    newSales: string;
    totalSpends: string;
  };
  orders?: number;
  customers?: number;
  sales?: number;
  updateDate: number;
  currency?: string;
  providerCurrency?: string;
  newUsers?: number;
  additionalSales?: number;
  totalSpend?: number;
}

const Stat = ({ string, value }: { string: string; value: ReactNode }) => (
  <Box display="flex" flexDirection="column" minWidth={120}>
    <Box>
      <Typography variant="bodyS">{string}</Typography>
    </Box>
    <Box>
      <Typography variant="h4">{value}</Typography>
    </Box>
  </Box>
);
const formatNumber = (value: number | undefined | null) => (!value ? '-' : String(value));

export const PromotionStatsFC = ({
  strings,
  orders,
  customers,
  sales,
  currency,
  updateDate,
  providerCurrency,
  newUsers,
  additionalSales,
  totalSpend,
}: PromotionStatsFCProps) => {
  const intl = useIntl();

  const formattedSales = useMemo(
    () =>
      intl.formatNumber(sales ?? 0, {
        currency: currency || providerCurrency,
        style: 'currency',
      }),
    [providerCurrency, currency, intl, sales],
  );
  const formattedAdditionalSales = useMemo(
    () =>
      intl.formatNumber(additionalSales ?? 0, {
        currency: currency || providerCurrency,
        style: 'currency',
      }),
    [additionalSales, currency, intl, providerCurrency],
  );
  const formattedTotalSpends = useMemo(
    () =>
      intl.formatNumber(totalSpend ?? 0, {
        currency: currency || providerCurrency,
        style: 'currency',
      }),
    [totalSpend, currency, intl, providerCurrency],
  );

  const lastOrderInfo = useMemo(() => {
    if (!orders) {
      return strings.noOrders;
    }

    const formattedUpdateDate = DateTime.fromSeconds(updateDate).toFormat('MMM dd, yyyy, HH:mm');
    return `${strings.update} ${formattedUpdateDate}`;
  }, [updateDate, strings, orders]);

  return (
    <Box paddingBottom={4}>
      <Grid container marginLeft="2px" direction="column">
        <Grid container xs={12} direction="row" marginBottom={2}>
          <Grid item xs={4}>
            <Stat string={strings.orders} value={formatNumber(orders)} />
          </Grid>
          <Grid item xs={4}>
            <Stat string={strings.customers} value={formatNumber(customers)} />
          </Grid>
          <Grid item xs={4}>
            <Stat string={strings.newCustomers} value={formatNumber(newUsers)} />
          </Grid>
        </Grid>
        <Grid container xs={12} direction="row" marginBottom={2}>
          <Grid item xs={4}>
            <Stat string={strings.sales} value={formattedSales} />
          </Grid>
          <Grid item xs={4}>
            <Stat string={strings.newSales} value={formattedAdditionalSales} />
          </Grid>
          <Grid item xs={4}>
            <Stat string={strings.totalSpends} value={formattedTotalSpends} />
          </Grid>
        </Grid>
      </Grid>
      <Typography variant="caption" fontSize="14px">
        {lastOrderInfo}
      </Typography>
    </Box>
  );
};
